import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Globe } from 'lucide-react';
import GrantLavaLampOrbs from '../GrantLavaLampOrbs';
import MarshallAudioPlayer from '../MarshallAudioPlayer';

export default function GoldenRule() {
  const navigate = useNavigate();

  const traditions = [
    {
      name: 'CHRISTIANITY',
      quote: 'Do unto others as you would have them do unto you; for this is the law and the prophets.',
      source: 'Jesus, Matthew 7:12',
      color: 'from-blue-900/20 to-indigo-900/20',
      borderColor: 'border-blue-700/30'
    },
    {
      name: 'HINDUISM',
      quote: 'This is the sum of duty: Do not do to others what would cause pain if done to you.',
      source: 'Mahabharata 5.15.17',
      color: 'from-orange-900/20 to-amber-900/20',
      borderColor: 'border-orange-700/30'
    },
    {
      name: 'BUDDHISM',
      quote: 'Treat not others in ways that you yourself would find hurtful.',
      source: 'Udana-Vagga 5.18',
      color: 'from-yellow-900/20 to-orange-900/20',
      borderColor: 'border-yellow-700/30'
    },
    {
      name: 'CONFUCIANISM',
      quote: 'One word which sums up the basis of all good conduct... loving-kindness. Do not do to others what you would not want done to yourself.',
      source: 'Analects 15.23',
      color: 'from-red-900/20 to-pink-900/20',
      borderColor: 'border-red-700/30'
    },
    {
      name: 'BAHA\'I FAITH',
      quote: 'Lay not on any soul a load that you would not wish to bear yourself, and desire not for anyone the things that would not be good for yourself.',
      source: 'Gleanings',
      color: 'from-purple-900/20 to-pink-900/20',
      borderColor: 'border-purple-700/30'
    },
    {
      name: 'ISLAM',
      quote: 'Not one of you truly believes until you wish for others what you wish for yourself.',
      source: 'The Prophet Muhammad, Hadith',
      color: 'from-green-900/20 to-emerald-900/20',
      borderColor: 'border-green-700/30'
    },
    {
      name: 'TAOISM',
      quote: 'Regard your neighbour\'s gain as your own gain, and your neighbour\'s loss as your own loss.',
      source: 'Tao Te Ching, Chapter 53',
      color: 'from-teal-900/20 to-cyan-900/20',
      borderColor: 'border-teal-700/30'
    },
    {
      name: 'JUDAISM',
      quote: 'What is hateful to you, do not do to your neighbour. This is the whole Torah; all the rest is commentary.',
      source: 'Talmud, Shabbat 31a',
      color: 'from-blue-900/20 to-indigo-900/20',
      borderColor: 'border-blue-700/30'
    },
    {
      name: 'SIKHISM',
      quote: 'I am a stranger to no one and no one is a stranger to me. Indeed, I am a friend to all.',
      source: 'Guru Granth Sahib, p. 1299',
      color: 'from-indigo-900/20 to-purple-900/20',
      borderColor: 'border-indigo-700/30'
    },
    {
      name: 'JAINISM',
      quote: 'One should treat all creatures in the world as one would like to be treated.',
      source: 'Sutrakritanga',
      color: 'from-violet-900/20 to-purple-900/20',
      borderColor: 'border-violet-700/30'
    },
    {
      name: 'ZOROASTRIANISM',
      quote: 'Do not do unto others whatever is injurious to yourself.',
      source: 'Shayast-na-Shayast 13.29',
      color: 'from-fuchsia-900/20 to-pink-900/20',
      borderColor: 'border-fuchsia-700/30'
    },
    {
      name: 'NATIVE SPIRITUALITY',
      quote: 'We are as much alive as we keep the earth alive and heal her.',
      source: '',
      color: 'from-emerald-900/20 to-green-900/20',
      borderColor: 'border-emerald-700/30'
    },
    {
      name: 'UNITARIANISM',
      quote: 'We affirm and promote respect for the interdependent web of all existence.',
      source: '',
      color: 'from-cyan-900/20 to-blue-900/20',
      borderColor: 'border-cyan-700/30'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">The Golden Rule</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 9</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 relative">
        {/* Grant's Lava Lamp Orbs Background */}
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
          <GrantLavaLampOrbs width={typeof window !== 'undefined' ? window.innerWidth : 1920} height={typeof window !== 'undefined' ? window.innerHeight : 1080} />
        </div>

        <div className="max-w-6xl mx-auto relative" style={{ zIndex: 10 }}>
          {/* Introduction */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 drop-shadow-lg">The Golden Rule Across World Traditions</h2>
            <p className="text-xl text-slate-100 leading-relaxed max-w-3xl mx-auto drop-shadow-md mb-6">
              The principle of treating others as we wish to be treated transcends cultures, religions, and time. 
              It is humanity's shared wisdom, expressed in the words of every major spiritual tradition.
            </p>
            
            {/* Marshall's Audio Player */}
            <div className="max-w-2xl mx-auto mt-6">
              <MarshallAudioPlayer />
            </div>
          </div>

          {/* Traditions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {traditions.map((tradition, idx) => (
              <div
                key={idx}
                className={`bg-gradient-to-br ${tradition.color} border ${tradition.borderColor} rounded-lg p-6 hover:scale-[1.02] transition-transform backdrop-blur-md shadow-2xl`}
                data-testid={`tradition-${idx}`}
                style={{ backgroundColor: 'rgba(15, 23, 42, 0.75)' }}
              >
                <h3 className="text-xl font-bold text-white mb-4">{tradition.name}</h3>
                <blockquote className="text-slate-100 leading-relaxed mb-3 text-lg">
                  "{tradition.quote}"
                </blockquote>
                {tradition.source && (
                  <p className="text-slate-300 text-sm italic">— {tradition.source}</p>
                )}
              </div>
            ))}
          </div>

          {/* Reflection */}
          <div className="mt-12 bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-700/30 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-white mb-4">Universal Truth</h2>
            <p className="text-slate-200 leading-relaxed text-lg">
              Despite differences in language, culture, and ritual, all of humanity's wisdom traditions converge 
              on this single principle: <span className="font-semibold text-white">treat others with the same care and respect you desire for yourself</span>. 
              This is the foundation of Nonviolent Communication and compassionate living.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
